import axios from 'axios'
import { store } from '../utility/createStore'
import { refreshTokens } from '../actions/users'
import { invokeNotification } from '../actions/global-notification'
import { toggleModal } from '../actions/modal'
import { signOut } from '../actions/users'

const baseUrl = process.env.REACT_APP_API_URL
const AUTH_ERROR_MARKER = 'liputon-auth-handled'

const instance = axios.create({
  withCredentials: true
})

// Single-flight refresh so concurrent 401s share one auth/refresh call.
let refreshPromise = null

const resetSessionToLoginModal = (message = 'registration.invalid_login') => {
  const currentLocation = store.getState().router.location.pathname

  store.dispatch(
    signOut({
      remote: false,
      redirectToHome: false,
      reload: false
    })
  )

  store.dispatch(toggleModal(true, currentLocation, message))
}

export const apiNotification = params => {
  store.dispatch(invokeNotification(params))
}

const markAuthErrorHandled = (error, reason = 'session_expired') => {
  const nextError = error || new Error(reason)
  nextError.code = nextError.code || 'AUTH_HANDLED'
  nextError[AUTH_ERROR_MARKER] = true
  nextError.authReason = reason

  return nextError
}

export const isHandledAuthError = error => {
  return Boolean(error && error[AUTH_ERROR_MARKER])
}

const refreshAccessToken = refresh_token => {
  if (!refreshPromise) {
    refreshPromise = instance
      .post(`${baseUrl}auth/refresh`, {
        refresh_token
      })
      .then(({ data }) => {
        store.dispatch(
          refreshTokens({
            access_token: data.tokens.access_token,
            id_token: data.tokens.id_token,
            refresh_token: data.tokens.refresh_token
          })
        )

        instance.defaults.headers.common['Authorization'] =
          'Bearer ' + data.tokens.access_token

        return data.tokens.access_token
      })
      .catch(refreshError => {
        if (isHandledAuthError(refreshError)) {
          throw refreshError
        }

        // Only force login on auth rejection. Transient network/5xx keep session.
        if (refreshError?.response?.status === 401) {
          resetSessionToLoginModal()
          throw markAuthErrorHandled(refreshError)
        }

        throw refreshError
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

// adds headers to all requests if they exists
instance.interceptors.request.use(
  config => {
    const tokens = store.getState().users.tokens
    const locale = store.getState().i18n.locale || 'fi'
    const addTokens = config.url.includes(baseUrl)

    // Send locale to api in every request
    if (locale) {
      config.headers['X-Locale'] = locale;
    }

    if (tokens && addTokens) {
      config.headers['Authorization'] = 'Bearer ' + tokens.access_token
    }

    return config
  },
  error => Promise.reject(error)
)

// handles token refreshing if they are expired
instance.interceptors.response.use(
  response => response,
  error => {
    const refresh_token = store.getState().users.tokens?.refresh_token

    const originalRequest = error.config
    const status = error?.response?.status

    if (!originalRequest || !status) {
      return Promise.reject(error)
    }

    // Check if refresh token fails
    if (originalRequest.url === `${baseUrl}auth/refresh`) {
      if (status === 401) {
        resetSessionToLoginModal()
        return Promise.reject(markAuthErrorHandled(error))
      }

      return Promise.reject(error)
    }

    // intercepts request if token is expired
    if (
      status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true

      // Always clear signed-in state before opening the modal. LoginModal only
      // renders when !signedIn && modalOpen; toggleModal alone is invisible.
      if (!store.getState().users.signedIn || !refresh_token) {
        const reason = store.getState().users.signedIn
          ? 'session_expired'
          : 'login_required'
        const message =
          reason === 'session_expired'
            ? 'registration.invalid_login'
            : 'login.login_needed'

        resetSessionToLoginModal(message)
        return Promise.reject(markAuthErrorHandled(error, reason))
      }

      return refreshAccessToken(refresh_token).then(accessToken => {
        originalRequest.headers['Authorization'] = 'Bearer ' + accessToken
        return instance(originalRequest)
      })
    }

    // Terminal 401 after refresh/retry — force visible login modal
    if (status === 401) {
      resetSessionToLoginModal()
      return Promise.reject(markAuthErrorHandled(error))
    }

    // reject all else scenarios
    return Promise.reject(error)
  }
)

/*  baseurl ends to /
*   using GET and POST url should be in like path/to/; not /path/to/

/*  queryparams should be an object
*   {param: value}
*   evaluating to url?param=value
*/
export const GET = (url, queryParams = {}, useBaseUrl = true) => {
  const urlPrefix = useBaseUrl ? baseUrl : ''

  return instance.get(urlPrefix + url, {
    params: queryParams
  })
}

export const DELETE = (url, queryParams = {}, useBaseUrl = true) => {
  const urlPrefix = useBaseUrl ? baseUrl : ''

  return instance.delete(urlPrefix + url, {
    params: queryParams
  })
}

/*
 *   body should be an object
 */
export const POST = (url, body, useBaseUrl = true) => {
  const urlPrefix = useBaseUrl ? baseUrl : ''

  return instance.post(urlPrefix + url, body)
}



/*
 *   body should be an object
 */
export const PUT = (url, body, useBaseUrl = true) => {
  const urlPrefix = useBaseUrl ? baseUrl : ''

  return instance.put(urlPrefix + url, body)
}
